import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CUSTOMER_ORDER_MOVES,
  CUSTOMER_ORDER_NEXT,
  PAYMENT_OPTION_LABELS,
  whatsappNumber,
  type CustomerOrderStatus,
  type OrderDetailDTO,
  type OrderRowDTO,
} from '@bifeai/shared';
import { Badge, Button, Callout, Dialog, EmptyState, Field, Input, PageHeader, Skeleton, useToast } from '@bifeai/ui';
import {
  ArrowRight,
  Bike,
  ClipboardList,
  Link2,
  MessageCircle,
  MessagesSquare,
  PackageX,
  Plus,
  Send,
  Settings2,
  ShoppingCart,
  Store,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { NewOrderDialog } from './NewOrderDialog';
import { SoldOutDialog } from './SoldOutDialog';
import { ORDER_TONES, orderLabel, phoneText, qtyText, whenText } from './shared';
import './orders-chat.css';
import { FranchisePrivate } from '../../components/FranchisePrivate';

const COLUMNS: { status: CustomerOrderStatus | 'DONE'; title: string }[] = [
  { status: 'NEW', title: 'Nuevos' },
  { status: 'PREPARING', title: 'Preparando' },
  { status: 'READY', title: 'Listos' },
  { status: 'DONE', title: 'Entregados o cancelados (24 h)' },
];
const NEXT_LABEL: Partial<Record<CustomerOrderStatus, string>> = { PREPARING: 'Empezar a preparar', READY: 'Marcar listo', DELIVERED: 'Entregado' };
/** El paso siguiente de cada pedido (la flecha de la tarjeta). */
const FORWARD: Partial<Record<CustomerOrderStatus, CustomerOrderStatus>> = { NEW: 'PREPARING', PREPARING: 'READY', READY: 'DELIVERED' };
const FORWARD_LABEL: Partial<Record<CustomerOrderStatus, string>> = { PREPARING: 'Preparar', READY: 'Listo', DELIVERED: 'Entregado' };
/** Estado al que pasa un pedido soltado en cada columna. */
const DROP_STATUS: Record<CustomerOrderStatus | 'DONE', CustomerOrderStatus> = {
  NEW: 'NEW',
  PREPARING: 'PREPARING',
  READY: 'READY',
  DONE: 'DELIVERED',
  DELIVERED: 'DELIVERED',
  CANCELED: 'CANCELED',
};

/** Texto del aviso "pedido listo" para mandar desde el WhatsApp del local (WhatsApp Web o la app). */
export function readyMessage(o: OrderDetailDTO, business: string): string {
  const first = (o.contactName ?? '').split(' ')[0] || '';
  const exact = o.finalTotalCents !== null || o.items.every((i) => !i.approximate);
  const total = `${formatMoney(o.finalTotalCents ?? o.estimatedTotalCents)}${exact ? '' : ' aprox.'}`;
  return o.fulfillment === 'DELIVERY'
    ? `Hola ${first}, te escribimos de ${business}. Tu pedido #${o.number} está listo y sale para tu casa. Total: ${total}. ¡Gracias!`
    : `Hola ${first}, te escribimos de ${business}. Tu pedido #${o.number} ya está listo. Total: ${total}. ¡Te esperamos!`;
}

/**
 * Tablero de pedidos (todos los planes): lo que se encarga en el mostrador o por teléfono y, con el adicional, lo que
 * entra por WhatsApp. De nuevo a entregado; se actualiza solo.
 */
export function OrdersPage() {
  const { privateFranchise } = useSession();
  if (privateFranchise) return <FranchisePrivate title="Pedidos" what="Los pedidos" />;
  return <OrdersPageContent />;
}

function OrdersPageContent() {
  const { can, me } = useSession();
  const chat = me.tenant.modules.includes('ORDERS_CHAT');
  const [params, setParams] = useSearchParams();
  const openId = params.get('p');
  const [creating, setCreating] = useState(params.get('nuevo') === '1');
  const [soldOut, setSoldOut] = useState(false);
  // Arrastrar una tarjeta (o tocar su flecha) la pasa de etapa, con las mismas reglas que el detalle.
  const [dragging, setDragging] = useState<OrderRowDTO | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<OrderRowDTO | null>(null);
  const board = useQuery({
    queryKey: ['orders-chat', 'orders'],
    queryFn: () => api.get<OrderRowDTO[]>('/orders-chat/orders'),
    // Mientras se arrastra no se actualiza: si la lista cambia en el medio, el navegador corta el arrastre.
    refetchInterval: dragging ? false : 10_000,
  });
  const rows = board.data ?? [];
  const qc = useQueryClient();
  const toast = useToast();
  const remove = useMutation({
    mutationFn: (o: OrderRowDTO) => api.del(`/orders-chat/orders/${o.id}`),
    onSuccess: (_r, o) => {
      setDeleting(null);
      if (openId === o.id) setParams({});
      void qc.invalidateQueries({ queryKey: ['orders-chat'] });
      toast.success(`Pedido #${o.number} borrado`);
    },
    onError: (err) => toast.error('No se pudo borrar el pedido', errorMessage(err)),
  });
  const move = useMutation({
    mutationFn: (m: { order: OrderRowDTO; status: CustomerOrderStatus }) =>
      api.patch<OrderDetailDTO>(`/orders-chat/orders/${m.order.id}/status`, { status: m.status }),
    onSuccess: (o) => {
      void qc.invalidateQueries({ queryKey: ['orders-chat'] });
      toast.success(`Pedido #${o.number}: ${orderLabel(o.status).toLowerCase()}`);
    },
    onError: (err) => toast.error('No se pudo pasar de etapa', errorMessage(err)),
  });
  const canDrop = (o: OrderRowDTO | null, col: CustomerOrderStatus | 'DONE') => !!o && CUSTOMER_ORDER_MOVES[o.status].includes(DROP_STATUS[col]);
  const by = (s: CustomerOrderStatus | 'DONE') =>
    rows.filter((o) => (s === 'DONE' ? o.status === 'DELIVERED' || o.status === 'CANCELED' : o.status === s));

  return (
    <>
      <PageHeader
        title="Pedidos"
        subtitle={
          chat
            ? 'Lo que entra por WhatsApp y lo que encargan en el mostrador o por teléfono. Lo que se pesa entra con precio aproximado: al prepararlo cargás el peso real y le mandás el total final.'
            : 'Los encargos del mostrador o por teléfono, de nuevo a entregado. Ya no más papelitos de pedidos en la pared ni cuaderno.'
        }
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {chat && (can('settings.manage') || can('franchise.accounts')) && (
              <Link to="/pedidos/configuracion" className="bf-btn bf-btn--sm">
                <Settings2 size={16} aria-hidden /> Configurar agente
              </Link>
            )}
            {chat && (
              <Button size="sm" icon={<PackageX />} onClick={() => setSoldOut(true)}>
                Hay / no hay
              </Button>
            )}
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setCreating(true)}>
              Nuevo pedido
            </Button>
          </div>
        }
      />
      {soldOut && <SoldOutDialog onClose={() => setSoldOut(false)} />}
      {board.isPending ? (
        <Skeleton style={{ height: 260 }} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ClipboardList />}
          title="Chau papelitos en la pared"
          text={
            chat
              ? 'Ya no más papelitos de pedidos en la pared ni cuaderno. Los pedidos de bifi por WhatsApp aparecen acá solos; los del mostrador o por teléfono los cargás con "Nuevo pedido".'
              : 'Ya no más papelitos de pedidos en la pared ni cuaderno. Cargá acá los encargos del mostrador o por teléfono con "Nuevo pedido" y seguilos hasta que se entregan.'
          }
          action={
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              Nuevo pedido
            </Button>
          }
        />
      ) : (
        <div className="bf-board">
          {COLUMNS.map((col) => {
            const list = by(col.status);
            const droppable = canDrop(dragging, col.status);
            return (
              <section
                key={col.status}
                className={`bf-board-col is-${col.status.toLowerCase()}${dragging && droppable ? ' can-drop' : ''}${dragging && !droppable && !list.some((x) => x.id === dragging.id) ? ' no-drop' : ''}${over === col.status && droppable ? ' is-over' : ''}`}
                aria-label={col.title}
                onDragOver={(e) => {
                  if (!droppable) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (over !== col.status) setOver(col.status);
                }}
                onDragLeave={(e) => {
                  // Pasar por encima de una tarjeta de la columna no es salir de la columna.
                  if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                  setOver((v) => (v === col.status ? null : v));
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(null);
                  if (dragging && droppable) move.mutate({ order: dragging, status: DROP_STATUS[col.status] });
                  setDragging(null);
                }}
              >
                <h3>
                  <span>
                    <i className="bf-board-dot" aria-hidden /> {col.title}
                  </span>
                  <span className="bf-board-count">{list.length}</span>
                </h3>
                {list.length === 0 && <p className="bf-board-empty">{dragging && droppable ? 'Soltalo acá' : 'Sin pedidos'}</p>}
                {list.map((o) => (
                  <article
                    key={o.id}
                    className={`bf-order-card${dragging?.id === o.id ? ' is-dragging' : ''}`}
                    role="button"
                    tabIndex={0}
                    draggable={FORWARD[o.status] !== undefined}
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', o.id);
                      // Se marca en el cuadro siguiente: cambiar la página en el mismo instante a veces corta el arrastre.
                      requestAnimationFrame(() => setDragging(o));
                    }}
                    onDragEnd={() => {
                      setDragging(null);
                      setOver(null);
                    }}
                    onClick={() => setParams({ p: o.id })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setParams({ p: o.id });
                      }
                    }}
                  >
                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <strong>#{o.number}</strong>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Badge tone={ORDER_TONES[o.status]}>{orderLabel(o.status)}</Badge>
                        <button
                          type="button"
                          className="bf-order-del"
                          title="Borrar pedido"
                          aria-label={`Borrar el pedido #${o.number}`}
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleting(o);
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <Trash2 size={14} aria-hidden />
                        </button>
                      </span>
                    </span>
                    {chat && <span className="bf-order-origin">{o.createdBy === 'STAFF' ? 'Mostrador' : 'WhatsApp · bifi'}</span>}
                    <span className="bf-cell-main">{o.contactName ?? 'Cliente'}</span>
                    <span className="bf-cell-sub">
                      {o.fulfillment === 'DELIVERY' ? <Bike size={13} aria-hidden /> : <Store size={13} aria-hidden />} {whenText(o.scheduledFor)}
                    </span>
                    <span className="bf-cell-sub">
                      {o.itemsCount} {o.itemsCount === 1 ? 'producto' : 'productos'} ·{' '}
                      {o.finalTotalCents !== null ? `Total ${formatMoney(o.finalTotalCents)}` : `≈ ${formatMoney(o.estimatedTotalCents)}`}
                    </span>
                    {o.paidAt && <span className="bf-cell-sub">Pagado con Mercado Pago</span>}
                    {rows.some((r) => r.branchId !== o.branchId) && <span className="bf-cell-sub">{o.branchName}</span>}
                    {FORWARD[o.status] && (
                      <button
                        type="button"
                        className="bf-order-next"
                        disabled={move.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          move.mutate({ order: o, status: FORWARD[o.status]! });
                        }}
                      >
                        {FORWARD_LABEL[FORWARD[o.status]!]} <ArrowRight size={14} aria-hidden />
                      </button>
                    )}
                  </article>
                ))}
              </section>
            );
          })}
        </div>
      )}
      {openId && <OrderDialog id={openId} onClose={() => setParams({})} />}
      <Dialog
        open={deleting !== null}
        onOpenChange={(v) => !remove.isPending && !v && setDeleting(null)}
        title={deleting ? `¿Borrar el pedido #${deleting.number}?` : ''}
        description="Sale del tablero y no se puede recuperar. Al cliente no le llega ningún aviso: si querés avisarle, cancelalo en vez de borrarlo. Si ya se cobró en la caja, la venta queda igual."
      >
        <div className="bf-form-actions">
          <Button onClick={() => setDeleting(null)} disabled={remove.isPending}>
            No, dejarlo
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => deleting && remove.mutate(deleting)}>
            Borrar pedido
          </Button>
        </div>
      </Dialog>
      {creating && (
        <NewOrderDialog
          onClose={() => {
            setCreating(false);
            if (params.get('nuevo')) setParams({});
          }}
          onCreated={(o) => {
            setCreating(false);
            setParams({ p: o.id });
          }}
        />
      )}
    </>
  );
}

function OrderDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { can, me } = useSession();
  const detail = useQuery({ queryKey: ['orders-chat', 'order', id], queryFn: () => api.get<OrderDetailDTO>(`/orders-chat/orders/${id}`) });
  const [weights, setWeights] = useState<Record<string, string>>({});
  const [cancelReason, setCancelReason] = useState<string | null>(null);
  const done = (o: OrderDetailDTO, msg?: string) => {
    qc.setQueryData(['orders-chat', 'order', id], o);
    void qc.invalidateQueries({ queryKey: ['orders-chat'] });
    if (msg) toast.success(msg);
  };
  const status = useMutation({
    mutationFn: (s: { status: CustomerOrderStatus; reason?: string }) => api.patch<OrderDetailDTO>(`/orders-chat/orders/${id}/status`, s),
    onSuccess: (o) => {
      setCancelReason(null);
      done(o, `Pedido #${o.number}: ${orderLabel(o.status).toLowerCase()}`);
    },
    onError: (err) => toast.error('No se pudo cambiar', errorMessage(err)),
  });
  const save = useMutation({
    mutationFn: (items: { id: string; qtyActual: number }[]) => api.put<OrderDetailDTO>(`/orders-chat/orders/${id}/weights`, { items }),
    onSuccess: (o) => {
      setWeights({});
      done(o, 'Pesos guardados');
    },
    onError: (err) => toast.error('No se pudieron guardar', errorMessage(err)),
  });
  const sendFinal = useMutation({
    mutationFn: () => api.post<OrderDetailDTO>(`/orders-chat/orders/${id}/send-final`),
    onSuccess: (o) => done(o, 'Le mandamos el total final al cliente'),
    onError: (err) => toast.error('No se pudo mandar', errorMessage(err)),
  });
  const notify = useMutation({
    mutationFn: () => api.post<OrderDetailDTO>(`/orders-chat/orders/${id}/notify`),
    onSuccess: (o) => done(o, 'Le avisamos al cliente por WhatsApp'),
    onError: (err) => toast.error('No se pudo avisar', errorMessage(err)),
  });
  const payLink = useMutation({
    mutationFn: () => api.post<OrderDetailDTO>(`/orders-chat/orders/${id}/payment-link`),
    onSuccess: (o) => done(o, 'Le mandamos el link de Mercado Pago al cliente'),
    onError: (err) => toast.error('No se pudo mandar el link', errorMessage(err)),
  });

  const o = detail.data;
  // El link va por el total final; si todo es de precio fijo, alcanza con el aproximado (que es exacto).
  const payable = o ? o.finalTotalCents !== null || o.items.every((i) => !i.approximate) : false;
  const open = o && ['NEW', 'PREPARING', 'READY'].includes(o.status);
  const parse = (unitType: 'WEIGHT' | 'UNIT', v: string) => {
    const n = Number(v.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) return null;
    return unitType === 'WEIGHT' ? Math.round(n * 1000) : Math.round(n);
  };
  const pending = o ? o.items.filter((i) => weights[i.id] !== undefined && weights[i.id] !== '') : [];

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      wide
      title={o ? `Pedido #${o.number}` : 'Pedido'}
      description={o ? `${o.contactName ?? 'Cliente'} · ${o.branchName}` : undefined}
    >
      {!o ? (
        <Skeleton style={{ height: 220 }} />
      ) : (
        <div className="bf-form">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Badge tone={ORDER_TONES[o.status]} dot>
              {orderLabel(o.status)}
            </Badge>
            <Badge>{o.fulfillment === 'DELIVERY' ? `Envío${o.zone ? ` · ${o.zone}` : ''}` : 'Retira en el local'}</Badge>
            <Badge>{whenText(o.scheduledFor)}</Badge>
            {o.paymentMethod && <Badge>{PAYMENT_OPTION_LABELS[o.paymentMethod]}</Badge>}
            {o.paidAt && (
              <Badge tone="success" dot>
                Pagado con Mercado Pago
              </Badge>
            )}
            {o.contactPhone && <Badge>{phoneText(o.contactPhone)}</Badge>}
          </div>
          {o.address && <p>Dirección: {o.address}</p>}
          {o.notes && (
            <Callout>
              {o.createdBy === 'STAFF' ? 'Notas' : 'Indicaciones del cliente'}: {o.notes}
            </Callout>
          )}
          {o.cancelReason && <Callout tone="danger">Cancelado: {o.cancelReason}</Callout>}
          <div className="bf-table-wrap">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Pidió</th>
                  <th>{open ? 'Pesó / contó' : 'Real'}</th>
                  <th className="bf-num">Importe</th>
                </tr>
              </thead>
              <tbody>
                {o.items.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <div className="bf-cell-main">{i.productName}</div>
                      <div className="bf-cell-sub">
                        {formatMoney(i.unitPriceCents)} {i.unitType === 'WEIGHT' ? 'el kilo' : 'la unidad'}
                        {i.approximate ? ' · se pesa' : ' · precio fijo'}
                      </div>
                    </td>
                    <td>{qtyText(i.unitType, i.qtyRequested)}</td>
                    <td style={{ maxWidth: 140 }}>
                      {open ? (
                        <Input
                          inputMode="decimal"
                          aria-label={`Peso real de ${i.productName}`}
                          placeholder={
                            i.qtyActual !== null
                              ? i.unitType === 'WEIGHT'
                                ? String(i.qtyActual / 1000).replace('.', ',')
                                : String(i.qtyActual)
                              : i.unitType === 'WEIGHT'
                                ? 'kg'
                                : 'unid.'
                          }
                          value={weights[i.id] ?? ''}
                          onChange={(e) => setWeights({ ...weights, [i.id]: e.target.value })}
                        />
                      ) : i.qtyActual !== null ? (
                        qtyText(i.unitType, i.qtyActual)
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="bf-num">
                      {i.finalTotalCents !== null ? (
                        formatMoney(i.finalTotalCents)
                      ) : (
                        <span className="bf-cell-sub">≈ {formatMoney(i.estimatedTotalCents)}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {o.deliveryFeeCents > 0 && (
                  <tr>
                    <td colSpan={3}>Envío</td>
                    <td className="bf-num">{formatMoney(o.deliveryFeeCents)}</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={3}>
                    <strong>{o.finalTotalCents !== null ? 'Total final' : 'Total aproximado'}</strong>
                  </td>
                  <td className="bf-num">
                    <strong>{formatMoney(o.finalTotalCents ?? o.estimatedTotalCents)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {open && pending.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                loading={save.isPending}
                onClick={() => {
                  const items = pending.map((i) => ({ id: i.id, qtyActual: parse(i.unitType, weights[i.id]!) }));
                  if (items.some((x) => x.qtyActual === null)) return toast.error('Revisá los pesos', 'Usá números, por ejemplo 2,140.');
                  save.mutate(items as { id: string; qtyActual: number }[]);
                }}
              >
                Guardar pesos
              </Button>
            </div>
          )}
          {open && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {o.conversationId && (
                <Button
                  variant="secondary"
                  icon={<Send />}
                  disabled={o.finalTotalCents === null}
                  loading={sendFinal.isPending}
                  onClick={() => sendFinal.mutate()}
                  title={o.finalTotalCents === null ? 'Primero cargá lo que pesó cada producto' : undefined}
                >
                  {o.finalSentAt ? 'Reenviar total final' : 'Enviar total final'}
                </Button>
              )}
              {o.conversationId && !o.paidAt && (
                <Button
                  variant="secondary"
                  icon={<Link2 />}
                  disabled={!payable}
                  loading={payLink.isPending}
                  onClick={() => payLink.mutate()}
                  title={payable ? 'Le manda al cliente un link de Mercado Pago por el total' : 'Primero cargá lo que pesó cada producto'}
                >
                  {o.paymentLinkSent ? 'Reenviar link de pago' : 'Enviar link de pago'}
                </Button>
              )}
              <NotifyButton order={o} business={me.tenant.name} loading={notify.isPending} onSend={() => notify.mutate()} />
              {can('pos.sell') && (
                <Button variant="secondary" icon={<ShoppingCart />} onClick={() => navigate(`/venta?pedido=${o.id}`)}>
                  Cobrar en caja
                </Button>
              )}
              {o.conversationId && (
                <Link to={`/chat?c=${o.conversationId}`} className="bf-btn">
                  <MessagesSquare size={16} aria-hidden /> Ver la conversación
                </Link>
              )}
            </div>
          )}
          {o.finalSentAt && <p className="bf-cell-sub">Total final enviado al cliente el {new Date(o.finalSentAt).toLocaleString('es-AR')}.</p>}
          {open && (
            <div
              style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid var(--bf-line)', paddingTop: 12 }}
            >
              {CUSTOMER_ORDER_NEXT[o.status]
                .filter((s) => s !== 'CANCELED')
                .map((s) => (
                  <Button
                    key={s}
                    variant={s === 'NEW' || (o.status === 'READY' && s === 'PREPARING') ? 'ghost' : 'primary'}
                    loading={status.isPending}
                    onClick={() => status.mutate({ status: s })}
                  >
                    {NEXT_LABEL[s] ?? `Volver a ${orderLabel(s).toLowerCase()}`}
                  </Button>
                ))}
              <Button variant="danger" onClick={() => setCancelReason('')}>
                Cancelar pedido
              </Button>
            </div>
          )}
          {cancelReason !== null && (
            <div className="bf-form" style={{ borderTop: '1px solid var(--bf-line)', paddingTop: 12 }}>
              <Field label="Motivo de la cancelación" hint="Se lo avisamos al cliente.">
                <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} maxLength={300} autoFocus />
              </Field>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" onClick={() => setCancelReason(null)}>
                  Volver
                </Button>
                <Button
                  variant="danger"
                  disabled={!cancelReason.trim()}
                  loading={status.isPending}
                  onClick={() => status.mutate({ status: 'CANCELED', reason: cancelReason })}
                >
                  Confirmar cancelación
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

/**
 * Avisarle al cliente que el pedido está listo. Con WhatsApp conectado sale desde la plataforma (en las 24 h como
 * mensaje común, después con la plantilla de Meta); si no, abre el WhatsApp del local con el mensaje escrito.
 */
function NotifyButton({ order: o, business, loading, onSend }: { order: OrderDetailDTO; business: string; loading: boolean; onSend: () => void }) {
  // Los pedidos de bifi ya tienen su conversación y sus avisos (total final, link de pago).
  if (o.createdBy === 'AGENT' || !o.contactPhone) return null;
  if (o.notify.inApp && (o.notify.windowOpen || o.notify.templateReady)) {
    return (
      <Button variant="secondary" icon={<MessageCircle />} loading={loading} onClick={onSend} title="Sale por el WhatsApp de la sucursal">
        Avisar por WhatsApp
      </Button>
    );
  }
  const n = whatsappNumber(o.contactPhone);
  if (!n) return null;
  return (
    <a
      className="bf-btn"
      href={`https://wa.me/${n}?text=${encodeURIComponent(readyMessage(o, business))}`}
      target="_blank"
      rel="noreferrer"
      title={
        o.notify.inApp
          ? 'La plantilla de Meta todavía no está aprobada: se abre tu WhatsApp con el mensaje escrito'
          : 'Se abre tu WhatsApp (o WhatsApp Web) con el mensaje escrito: solo tocás Enviar'
      }
    >
      <MessageCircle size={16} aria-hidden /> Avisar por WhatsApp Web
    </a>
  );
}
